import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	List,
	ListItemButton,
	ListItemText,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
	Assignment,
	AssignmentStatus,
	addPersonAssignment,
	getAssignments,
	PersonAssignmentCreateType,
	statusToThemeKey,
} from "../../api";
import { getAssignmentBoardConfigs } from "../../api/assignments/assignment-board-config";
import type { BoardColumnType, Status } from "../../api/assignments/board-types";
import { getPersonAssignments } from "../../api/assignments/person-assignment";
import { getVotes } from "../../api/vote";
import { useJobylonLinkedJobs } from "../../hooks/use-jobylon-linked-jobs";
import { formatRelativeTimeShort } from "../../utils/date-formatting";
import { boardHref } from "../boardv2/utils";
import { useGlobalState } from "../globalState";
import { AssignmentFilterBar, useAssignmentFilter } from "../shared/assignment-picker";
import { BoardStatusGrid } from "../shared/board-status-grid";
import { JobylonBadge } from "../shared/jobylon-badge";
import { JobylonPushDialog } from "./jobylon-push-dialog";

interface UpvoteInfo {
	assignmentId: number;
	username: string;
	created: string;
}

interface ExistingPlacement {
	column: string;
	statusStr: string;
}

interface AddToRoleDialogProps {
	personId: number;
	open: boolean;
	onClose: () => void;
	onAdded?: () => void;
	/** List this group's roles instead of the signed-in user's group's: the
	 *  candidate pool lists the pool's group, which staff can switch. */
	groupId?: number;
	/** Offer "Upvote" beside "Add to board" on each role. The parent drafts
	 *  the pitch and casts the vote; the dialog only picks the role. */
	onUpvote?: (assignment: Assignment) => void;
}

export const AddToRoleDialog = ({
	personId,
	open,
	onClose,
	onAdded,
	groupId,
	onUpvote,
}: AddToRoleDialogProps) => {
	const [assignments, setAssignments] = useState<Assignment[]>([]);
	const [upvotes, setUpvotes] = useState<UpvoteInfo[]>([]);
	const [placements, setPlacements] = useState<Map<number, ExistingPlacement>>(
		new Map()
	);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// The role whose board the person is being placed on, with its columns.
	const [placeOn, setPlaceOn] = useState<{
		assignment: Assignment;
		columns: BoardColumnType[];
	} | null>(null);
	const [placing, setPlacing] = useState(false);
	// A role in here has its real pipeline in Jobylon, so a person added to it
	// is pushed there and its links lead there.
	const jobylonByAssignment = useJobylonLinkedJobs();
	const [pushTarget, setPushTarget] = useState<{
		jobylonJobId: number;
		assignmentId: number;
		roleName: string;
	} | null>(null);
	const {
		searchQuery,
		setSearchQuery,
		activeStatusKeys,
		setActiveStatusKeys,
		filteredAssignments: eligibleAssignments,
	} = useAssignmentFilter(assignments);
	const { currentUser } = useGlobalState();
	const theme = useTheme();
	const normalizedQuery = searchQuery.trim().toLowerCase();

	useEffect(() => {
		if (!open) return;
		setIsLoading(true);
		setError(null);
		const assignmentsPromise =
			assignments.length === 0
				? getAssignments(
						undefined,
						groupId === undefined
							? { creatorInMyUpGroup: true }
							: { creatorUpGroup: groupId }
					)
				: Promise.resolve(assignments);
		Promise.all([
			assignmentsPromise,
			getVotes({
				person: personId,
				grade: "up",
				ordering: "-created",
			}),
			getPersonAssignments({ personIds: [personId] }),
		])
			.then(([assignmentsRes, votesRes, paRes]) => {
				if (assignments.length === 0) setAssignments(assignmentsRes);
				const seen = new Set<number>();
				const ordered: UpvoteInfo[] = [];
				for (const vote of votesRes.results) {
					if (!seen.has(vote.assignment)) {
						seen.add(vote.assignment);
						ordered.push({
							assignmentId: vote.assignment,
							username: vote.user_username ?? "",
							created: vote.created,
						});
					}
				}
				setUpvotes(ordered);
				const placementMap = new Map<number, ExistingPlacement>();
				for (const pa of paRes) {
					if (
						pa.assignment !== undefined &&
						!placementMap.has(pa.assignment)
					) {
						placementMap.set(pa.assignment, {
							column: pa.column,
							statusStr: pa.status_str ?? "",
						});
					}
				}
				setPlacements(placementMap);
			})
			.catch((e: Error) => setError(e.message))
			.finally(() => setIsLoading(false));
	}, [open, personId]);

	const eligibleById = new Map(eligibleAssignments.map((a) => [a.id, a]));
	const upvotedRows = upvotes
		.map((u) => {
			const assignment = eligibleById.get(u.assignmentId);
			return assignment ? { assignment, upvote: u } : null;
		})
		.filter(
			(row): row is { assignment: Assignment; upvote: UpvoteInfo } => row !== null
		);
	const upvotedIdSet = new Set(upvotedRows.map((row) => row.assignment.id));

	const otherAssignments = eligibleAssignments.filter((a) => !upvotedIdSet.has(a.id));

	/**
	 * A Jobylon-run role's board lives in Jobylon, not here, so adding someone
	 * to one means pushing them there — and that needs a stage, which is a
	 * question rather than a single click.
	 */
	const handlePick = (assignment: Assignment) => {
		const jobylonJob = jobylonByAssignment.get(assignment.id);
		if (jobylonJob !== undefined) {
			setPushTarget({
				jobylonJobId: jobylonJob.id,
				assignmentId: assignment.id,
				roleName: assignment.name,
			});
			return;
		}
		// Where on the board is the recruiter's call: the role's columns and
		// statuses, from its board config (every role is created with one).
		setError(null);
		getAssignmentBoardConfigs(assignment.id)
			.then((res) => {
				const columns = res.results[0]?.config?.columns;
				if (!columns?.length) {
					setError(`${assignment.name} has no board columns to add to`);
					return;
				}
				setPlaceOn({ assignment, columns });
			})
			.catch((e: Error) =>
				setError(`Could not load ${assignment.name}'s board: ${e.message}`)
			);
	};

	const addPersonToAssignment = async (
		assignmentId: number,
		column: BoardColumnType,
		status: Status
	) => {
		const data: PersonAssignmentCreateType = {
			column: column.name,
			status_str: status.name,
			assignment: assignmentId,
			person: personId,
			created_by: currentUser.id,
			assignee: currentUser.id,
		};

		await addPersonAssignment({ data });
		setPlacements((prev) => {
			const next = new Map(prev);
			next.set(assignmentId, { column: column.name, statusStr: status.name });
			return next;
		});
		onAdded?.();
	};

	const placeOnBoard = (column: BoardColumnType, status: Status) => {
		if (placeOn === null) return;
		const { assignment } = placeOn;
		setPlacing(true);
		setError(null);
		addPersonToAssignment(assignment.id, column, status)
			.then(() => setPlaceOn(null))
			.catch((e: Error) => {
				setPlaceOn(null);
				setError(`Could not add to ${assignment.name}: ${e.message}`);
			})
			.finally(() => setPlacing(false));
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth={onUpvote ? "md" : "xs"}
			fullWidth
			data-component="add-to-role-dialog"
		>
			<DialogTitle>{onUpvote ? "Add to role" : "Add to Board"}</DialogTitle>
			<DialogContent sx={{ p: 0 }}>
				<AssignmentFilterBar
					searchQuery={searchQuery}
					onSearchChange={setSearchQuery}
					activeStatusKeys={activeStatusKeys}
					onStatusKeysChange={setActiveStatusKeys}
					autoFocus
				/>
				{error && (
					<Alert
						severity="error"
						onClose={() => setError(null)}
						sx={{ mx: 2, mb: 1 }}
						data-component="add-to-role-error"
					>
						{error}
					</Alert>
				)}
				{isLoading ? (
					<Stack
						direction="row"
						spacing={2}
						alignItems="center"
						justifyContent="center"
						sx={{ py: 3 }}
						data-component="loading-assignments"
					>
						<CircularProgress size={20} />
						<Typography>Loading roles...</Typography>
					</Stack>
				) : eligibleAssignments.length === 0 ? (
					<Typography
						sx={{ p: 2, textAlign: "center" }}
						color="text.secondary"
						data-component="add-to-role-empty"
					>
						{normalizedQuery
							? "No roles match your search"
							: assignments.length === 0 && groupId !== undefined
								? "This group has no roles yet"
								: "No active roles available"}
					</Typography>
				) : (
					<List data-component="assignments-list">
						{[
							...upvotedRows.map((row) => ({
								assignment: row.assignment,
								upvote: row.upvote,
								keyPrefix: "upvoted",
								dataComponent: "assignment-list-item-upvoted",
							})),
							...otherAssignments.map((assignment) => ({
								assignment,
								upvote: null,
								keyPrefix: "other",
								dataComponent: "assignment-list-item",
							})),
						].map(({ assignment: a, upvote, keyPrefix, dataComponent }) => {
							const placement = placements.get(a.id);
							return (
								<ListItemButton
									key={`${keyPrefix}-${a.id}`}
									// With two actions the buttons below choose; the row does not.
									onClick={
										placement || onUpvote
											? undefined
											: () => handlePick(a)
									}
									disableRipple={onUpvote !== undefined}
									data-component={dataComponent}
									sx={
										onUpvote
											? {
													cursor: "default",
													"&:hover": {
														backgroundColor: "transparent",
													},
												}
											: placement
												? {
														pointerEvents: "none",
														"&:hover": {
															backgroundColor:
																"transparent",
														},
													}
												: undefined
									}
								>
									<ListItemText
										primary={a.name}
										slotProps={{
											secondary: { component: "div" },
										}}
										secondary={
											<Stack
												direction="row"
												spacing={0.5}
												alignItems="center"
												flexWrap="wrap"
												useFlexGap
											>
												{a.company_name && (
													<span>@{a.company_name}</span>
												)}
												{upvote && (
													<Chip
														size="small"
														variant="filled"
														color="primary"
														label={
															upvote.username
																? `Upvoted by ${upvote.username} · ${formatRelativeTimeShort(upvote.created)}`
																: `Upvoted · ${formatRelativeTimeShort(upvote.created)}`
														}
														data-component="upvoted-chip"
														sx={{
															height: 18,
															fontSize: "0.7rem",
														}}
													/>
												)}
												{placement && (
													<Chip
														size="small"
														variant="filled"
														color="secondary"
														label={`Already on this board: ${placement.column}${placement.statusStr ? ` · ${placement.statusStr}` : ""}`}
														data-component="already-on-board-chip"
														sx={{
															height: 18,
															fontSize: "0.7rem",
														}}
													/>
												)}
											</Stack>
										}
									/>
									{/* Follows the text and spans both its lines, so the
									    wordmark can be large enough to read instead of
									    being squeezed beside the name. */}
									{jobylonByAssignment.has(a.id) && (
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												alignSelf: "stretch",
												ml: 1.5,
											}}
										>
											<JobylonBadge
												height={26}
												href={
													jobylonByAssignment.get(a.id)
														?.board_url
												}
												title="This role's board is in Jobylon — open it there"
											/>
										</Box>
									)}
									{placement && (
										<Tooltip
											title={
												jobylonByAssignment.has(a.id)
													? "Open in Jobylon"
													: "Open board"
											}
										>
											<IconButton
												component="a"
												href={
													jobylonByAssignment.get(a.id)
														?.board_url ?? boardHref(a.id)
												}
												target="_blank"
												rel="noopener"
												size="small"
												data-component="open-board-button"
												onClick={(e) => e.stopPropagation()}
												sx={{ pointerEvents: "auto", ml: 1 }}
											>
												<OpenInNewIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									)}
									<Chip
										size="small"
										label={a.status_display}
										sx={{
											backgroundColor:
												theme.assignmentStatus[
													statusToThemeKey[
														AssignmentStatus[a.status]
													]
												],
											color: "common.black",
											ml: 1,
										}}
									/>
									{onUpvote && (
										<Stack
											direction="row"
											spacing={1}
											sx={{ ml: 2, flexShrink: 0 }}
											data-component="add-to-role-actions"
										>
											<Button
												size="small"
												variant="outlined"
												disabled={placement !== undefined}
												onClick={() => handlePick(a)}
												data-component="add-to-role-board-button"
											>
												Add to board
											</Button>
											<Button
												size="small"
												variant="contained"
												startIcon={<ThumbUpIcon />}
												disabled={upvote !== null}
												onClick={() => onUpvote(a)}
												data-component="add-to-role-upvote-button"
											>
												Upvote
											</Button>
										</Stack>
									)}
								</ListItemButton>
							);
						})}
					</List>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
			</DialogActions>
			{placeOn && (
				<Dialog
					open
					onClose={() => setPlaceOn(null)}
					maxWidth="xs"
					fullWidth
					data-component="add-to-board-placement-dialog"
				>
					<DialogTitle data-component="add-to-board-placement-title">
						Where on {placeOn.assignment.name}?
					</DialogTitle>
					<DialogContent
						sx={{ px: 2 }}
						data-component="add-to-board-placement-content"
					>
						{placing ? (
							<Stack
								direction="row"
								spacing={2}
								alignItems="center"
								sx={{ py: 2 }}
								data-component="add-to-board-placing"
							>
								<CircularProgress size={20} />
								<Typography>Adding to the board...</Typography>
							</Stack>
						) : (
							<BoardStatusGrid
								columns={placeOn.columns}
								currentStatus={null}
								onPick={placeOnBoard}
							/>
						)}
					</DialogContent>
					<DialogActions>
						<Button
							onClick={() => setPlaceOn(null)}
							disabled={placing}
							data-component="add-to-board-placement-cancel"
						>
							Back
						</Button>
					</DialogActions>
				</Dialog>
			)}
			{pushTarget && (
				<JobylonPushDialog
					open
					jobylonJobId={pushTarget.jobylonJobId}
					personId={personId}
					roleName={pushTarget.roleName}
					onClose={() => setPushTarget(null)}
					onPushed={() => {
						// The push also puts them on the ODI board, so the row
						// shows the same "already here" state as a plain add.
						setPlacements((prev) => {
							const next = new Map(prev);
							next.set(pushTarget.assignmentId, {
								column: "Jobylon applicant",
								statusStr: "Applicant",
							});
							return next;
						});
						onAdded?.();
					}}
				/>
			)}
		</Dialog>
	);
};
